import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Coins, TrendingDown, Clock, Users, ArrowRight, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext.js';
import { useNavigate } from 'react-router-dom';

export default function PPDBurnRateWidget() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/ppd/burn-rate');
        if (!cancelled) setData(res.data);
      } catch {
        // PPD might not be used by this tenant — silently skip
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Don't render if no PPD activity and no balance (tenant doesn't use PPD)
  if (loading || !data) return null;
  if (data.balance === 0 && data.consumption_30d === 0 && data.ppd_workers === 0) return null;

  const isExhausted = data.balance <= 0;
  const isLow = data.days_remaining !== null && data.days_remaining <= 7 && data.days_remaining > 0;

  return (
    <Card
      className={`card-industrial cursor-pointer hover:border-[#ffd108] transition-all ${isExhausted ? 'border-red-500/40' : isLow ? 'border-amber-500/40' : ''}`}
      onClick={() => navigate('/settings?tab=billing')}
      data-testid="ppd-burn-rate-widget"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Coins className="h-5 w-5 text-[#ffd108]" />
            PPD Credits
            {isExhausted && (
              <Badge className="bg-red-500/20 text-red-400 border border-red-500/30 text-[10px]">
                <AlertTriangle className="h-3 w-3 mr-1" /> Exhausted
              </Badge>
            )}
            {isLow && !isExhausted && (
              <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[10px]">
                Low
              </Badge>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" className="text-xs" data-testid="ppd-widget-go-billing">
            Manage <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Balance */}
          <div data-testid="ppd-widget-balance">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Balance</div>
            <div className={`text-2xl font-bold font-mono ${isExhausted ? 'text-red-400' : isLow ? 'text-amber-400' : 'text-white'}`}>
              {data.balance < 0 ? `−${Math.abs(data.balance)}` : data.balance}
            </div>
          </div>

          {/* Burn Rate (7d average) */}
          <div data-testid="ppd-widget-burn-rate">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1">
              <TrendingDown className="h-3 w-3" /> Daily Usage
            </div>
            <div className="text-2xl font-bold font-mono text-white">
              {data.avg_daily_7d}
            </div>
            <div className="text-[10px] text-muted-foreground">credits/day (7d avg)</div>
          </div>

          {/* Days Remaining */}
          <div data-testid="ppd-widget-days-remaining">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Est. Remaining
            </div>
            <div className={`text-2xl font-bold font-mono ${data.days_remaining === null ? 'text-muted-foreground' : data.days_remaining <= 7 ? 'text-amber-400' : 'text-white'}`}>
              {data.days_remaining !== null ? `${data.days_remaining}d` : '—'}
            </div>
            <div className="text-[10px] text-muted-foreground">at current pace</div>
          </div>

          {/* PPD Workers */}
          <div data-testid="ppd-widget-workers">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1">
              <Users className="h-3 w-3" /> PPD Workers
            </div>
            <div className="text-2xl font-bold font-mono text-white">{data.ppd_workers}</div>
          </div>
        </div>

        {/* 30d summary bar */}
        <div className="mt-4 pt-3 border-t border-muted flex items-center justify-between text-xs text-muted-foreground">
          <span>Last 30 days: <span className="text-red-400 font-medium">{data.consumption_30d} used</span></span>
          <span><span className="text-green-400 font-medium">+{data.purchased_30d} purchased</span></span>
        </div>
      </CardContent>
    </Card>
  );
}
